import React, { useEffect, useState, useContext } from 'react';
import '../../css/Dashboard.css';
import { fetchTodayAttendance } from '../../api/Attendance';
import { Attendance } from '../../types/Attendance';
import { AuthContext } from '../../context/AuthContext';
import { usePermissions, useRole } from '../../hooks/usePermissions';
import * as MdIcons from 'react-icons/md';

const MdCheckCircle = MdIcons.MdCheckCircle as unknown as React.FC;
const MdCancel = MdIcons.MdCancel as unknown as React.FC;
const MdAccessTime = MdIcons.MdAccessTime as unknown as React.FC;
const MdEventBusy = MdIcons.MdEventBusy as unknown as React.FC;

const AttendanceComponent = () => {
  const { user } = useContext(AuthContext);
  const permissions = usePermissions();
  const role = useRole();
  const [todayAttendances, setTodayAttendances] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTodayAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTodayAttendance = async () => {
    try {
      setLoading(true);
      let data;
      
      if (permissions.canViewAllAttendance) {
        // Admin: fetch all today's attendance
        data = await fetchTodayAttendance();
      } else {
        // Non-admin: fetch own (or team for managers)
        const currentUserId = user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : 0;
        if (role === 'sales-manager' || role === 'ops') {
          // For managers, backend should return team attendance when includeTeam=true
          // For now, fetch own attendance
          data = await fetchTodayAttendance(currentUserId);
        } else {
          // Sales: only own
          data = await fetchTodayAttendance(currentUserId);
        }
      }
      
      setTodayAttendances(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load today attendance:', error);
      setTodayAttendances([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'present':
        return <span className="crm-status-icon present"><MdCheckCircle /></span>;
      case 'absent':
        return <span className="crm-status-icon absent"><MdCancel /></span>;
      case 'half-day':
        return <span className="crm-status-icon half-day"><MdAccessTime /></span>;
      case 'leave':
        return <span className="crm-status-icon leave"><MdEventBusy /></span>;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ');
  };

  const formatTime = (time?: string) => {
    if (!time) return '—';
    return time.substring(0, 5); // HH:MM format
  };

  const totalToday = todayAttendances.length;
  const presentCount = todayAttendances.filter(a => a.status === 'present').length;
  const attendancePercent = totalToday > 0 ? Math.round((presentCount / totalToday) * 100) : 0;

  return (
    <div>
      <div className="db-card-kpi">
        <div style={{ flex: 1 }}>
          <div className="db-card-kpi-meta">Attendance Today</div>
          <div className="db-card-kpi-value">
            {presentCount}/{totalToday}
          </div>
        </div>
        <div className="badge">
          {attendancePercent}% present
        </div>
      </div>
      {loading ? (
        <div className="db-empty">Loading...</div>
      ) : todayAttendances.length === 0 ? (
        <div className="db-empty">No attendance records for today</div>
      ) : (
        <div>
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Check-in</th>
                <th>Check-out</th>
              </tr>
            </thead>
            <tbody>
              {todayAttendances.slice(0, 5).map((attendance, i) => (
                <tr key={attendance.id || i}>
                  <td>
                    <div className="crm-user-cell">
                      <div className="crm-user-name">{attendance.userName || `User ${attendance.userId}`}</div>
                      {attendance.userEmail && (
                        <div className="crm-user-email">{attendance.userEmail}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="crm-status">
                      {getStatusIcon(attendance.status)}
                      <span>{getStatusLabel(attendance.status)}</span>
                    </div>
                  </td>
                  <td>{formatTime(attendance.checkInTime)}</td>
                  <td>{formatTime(attendance.checkOutTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {todayAttendances.length > 5 && (
            <div className="db-more">
              +{todayAttendances.length - 5} more
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceComponent;
